import { useIntl } from '@capsicum/common';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export type UkSelfCategorisationRestrictedInvestorFormType = z.infer<
	ReturnType<typeof useUkSelfCategorisationRestrictedInvestorSchema>
>;

const LessOrMoreOptions = {
	LESS: true,
	MORE: false
} as const;

const useUkSelfCategorisationRestrictedInvestorSchema = () => {
	const { $t } = useIntl();

	return z.object({
		restrictedHasInvestedPercent: z
			.string()
			.min(1, $t('requirements.common.form.errors.emptyValue'))
			.refine(
				val => {
					const regex = /^(0|[1-9][0-9]*)(\.\d{1,2})?$/;

					return regex.test(val);
				},
				{
					message: $t('requirements.common.form.errors.notValidNumber')
				}
			)
			.refine(
				val => {
					const parsedVal = parseFloat(val);

					return parsedVal >= 0.01 && parsedVal <= 100;
				},
				{
					message: $t('jurisdictionRequirements.ukSelfCategorisation.newErrorPercentageMessage')
				}
			),
		restrictedWillInvestPercent: z
			.string()
			.min(1, $t('requirements.common.form.errors.emptyValue'))
			.refine(
				val => {
					const regex = /^(0|[1-9][0-9]*)(\.\d{1,2})?$/;

					return regex.test(val);
				},
				{
					message: $t('requirements.common.form.errors.notValidNumber')
				}
			)
			.refine(
				val => {
					const parsedVal = parseFloat(val);

					return parsedVal >= 0.01 && parsedVal <= 100;
				},
				{
					message: $t('jurisdictionRequirements.ukSelfCategorisation.newErrorPercentageMessage')
				}
			)
	});
};

const useUkSelfCategorisationRestrictedInvestorForm = () => {
	const ukSelfCategorisationRestrictedInvestorForm = useForm<UkSelfCategorisationRestrictedInvestorFormType>({
		defaultValues: useMemo(
			() => ({
				restrictedHasInvestedPercent: '',
				restrictedWillInvestPercent: ''
			}),
			[]
		),
		mode: 'onChange',
		resolver: zodResolver(useUkSelfCategorisationRestrictedInvestorSchema())
	});

	return ukSelfCategorisationRestrictedInvestorForm;
};

export const useUkSelfCategorisationRestrictedInvestor = () => {
	const [restrictedHasInvestedLessThanTenPercent, setRestrictedHasInvestedLessThanTenPercent] = useState<
		boolean | null
	>(null);
	const [restrictedWillInvestLessThanTenPercent, setRestrictedWillInvestLessThanTenPercent] = useState<
		boolean | null
	>(null);

	const { $t } = useIntl();
	const ukSelfCategorisationRestrictedInvestorForm = useUkSelfCategorisationRestrictedInvestorForm();

	const userHasInvestedLessOptions = [
		{
			id: LessOrMoreOptions.LESS,
			label: $t('jurisdictionRequirements.ukSelfCategorisation.restrictedHasInvestedLessThanTenPercentOptionsYes')
		},
		{
			id: LessOrMoreOptions.MORE,
			label: $t('jurisdictionRequirements.ukSelfCategorisation.restrictedHasInvestedLessThanTenPercentOptionsNo')
		}
	];

	const userWillInvestLessOptions = [
		{
			id: LessOrMoreOptions.LESS,
			label: $t(
				'jurisdictionRequirements.ukSelfCategorisation.restrictedWillIntendToInvestLessThanTenPercentOptionsYes'
			)
		},
		{
			id: LessOrMoreOptions.MORE,
			label: $t(
				'jurisdictionRequirements.ukSelfCategorisation.restrictedWillIntendToInvestLessThanTenPercentOptionsNo'
			)
		}
	];

	const isRestrictedHasInvestedPercentValid =
		!ukSelfCategorisationRestrictedInvestorForm.formState.errors.restrictedHasInvestedPercent;
	const isRestrictedWillInvestPercentValid =
		!ukSelfCategorisationRestrictedInvestorForm.formState.errors.restrictedWillInvestPercent;

	const restrictedHasInvestedPercentValue =
		ukSelfCategorisationRestrictedInvestorForm.watch('restrictedHasInvestedPercent');
	const restrictedWillInvestPercentValue =
		ukSelfCategorisationRestrictedInvestorForm.watch('restrictedWillInvestPercent');

	const setUserHasInvestedPercentage = useCallback(
		(value: boolean) => {
			if (!value) {
				ukSelfCategorisationRestrictedInvestorForm.setValue('restrictedHasInvestedPercent', '');
				ukSelfCategorisationRestrictedInvestorForm.clearErrors('restrictedHasInvestedPercent');
			}
			setRestrictedHasInvestedLessThanTenPercent(value);
		},
		[setRestrictedHasInvestedLessThanTenPercent, ukSelfCategorisationRestrictedInvestorForm]
	);

	const setUserWillInvestPercentage = useCallback(
		(value: boolean) => {
			if (!value) {
				ukSelfCategorisationRestrictedInvestorForm.setValue('restrictedWillInvestPercent', '');
				ukSelfCategorisationRestrictedInvestorForm.clearErrors('restrictedWillInvestPercent');
			}
			setRestrictedWillInvestLessThanTenPercent(value);
		},
		[setRestrictedWillInvestLessThanTenPercent, ukSelfCategorisationRestrictedInvestorForm]
	);

	const hasInvestedValid = useMemo(() => {
		return (
			restrictedHasInvestedLessThanTenPercent === false ||
			(restrictedHasInvestedLessThanTenPercent === true &&
				isRestrictedHasInvestedPercentValid &&
				restrictedHasInvestedPercentValue !== '')
		);
	}, [
		isRestrictedHasInvestedPercentValid,
		restrictedHasInvestedPercentValue,
		restrictedHasInvestedLessThanTenPercent
	]);

	const willInvestValid = useMemo(() => {
		return (
			restrictedWillInvestLessThanTenPercent === false ||
			(restrictedWillInvestLessThanTenPercent === true &&
				isRestrictedWillInvestPercentValid &&
				restrictedWillInvestPercentValue !== '')
		);
	}, [isRestrictedWillInvestPercentValid, restrictedWillInvestLessThanTenPercent, restrictedWillInvestPercentValue]);

	const isRestrictedFormValid = useMemo(() => {
		return hasInvestedValid && willInvestValid;
	}, [hasInvestedValid, willInvestValid]);

	return {
		isRestrictedFormValid,
		restrictedHasInvestedLessThanTenPercent,
		restrictedHasInvestedPercentValue,
		restrictedWillInvestLessThanTenPercent,
		restrictedWillInvestPercentValue,
		setUserHasInvestedPercentage,
		setUserWillInvestPercentage,
		ukSelfCategorisationRestrictedInvestorForm,
		userHasInvestedLessOptions,
		userWillInvestLessOptions
	};
};
